import { NoodleQuestionDto } from '@/app/common/general-types/noodle.dto';
import { ScreeningResultsResponse } from '@/app/common/general-types/social-screening.dto';
import {
  EMPTY_PROGRESS,
  FlaggedCohortContent,
  FlagSeverity,
  RuleAttribution,
  RuleOutcome,
  ContentView,
  ScreeningContentItem,
  ScreeningCreatorProgress,
  ScreeningFlag,
  ScreeningKpis,
  ScreeningMediaKind,
  ScreeningVerdict,
} from './screening-results.model';

/*
 * Pure, Angular-free translation of Noodle's screening payloads into the view model.
 *
 * The brief-level payload (`screening_results?briefId&issuesOnly=true`) is untyped in both repos —
 * nothing declares its shape, and it may carry only counts. So everything here narrows `unknown`
 * defensively and is total: a missing field yields `undefined`, never a fabricated value, and a
 * completely unrecognised payload yields an empty list rather than an exception.
 *
 * When the real payload is confirmed, THIS FILE is the only thing that changes.
 */

/* ---- narrowing helpers ------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Reads a string property, treating blank strings as absent. */
function readString(source: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

function readNumber(source: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return undefined;
}

function readArray(source: Record<string, unknown>, ...keys: string[]): unknown[] {
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

/* ---- derivations ------------------------------------------------------- */

/**
 * Noodle does not send a severity, so it comes from how the rule was classified when the brief was
 * extracted: a mandatory rule failing matters more than an optional one.
 */
export function deriveSeverity(rule: NoodleQuestionDto | undefined): FlagSeverity {
  switch (rule?.questionSubType) {
    case 'mandatory':
      return 'high';
    case 'conditional':
      return 'medium';
    case 'optional':
      return 'low';
    default:
      // No rule resolved, or Noodle omitted the sub-type. Defaulting to 'medium' would silently
      // mislabel every such flag, so say nothing instead — the view omits the pill.
      return 'unrated';
  }
}

/** A `do` that was not satisfied is a missing requirement; a `dont` that fired is a breach. */
export function deriveKind(rule: NoodleQuestionDto | undefined): 'breach' | 'missing' {
  return rule?.type === 'do' ? 'missing' : 'breach';
}

/**
 * The five verdict states, from counts alone.
 *
 * Note we cannot currently distinguish "failed" from "flagged": that would need to know whether a
 * *mandatory* rule went unmet, which requires per-rule detail. Until the payload carries it,
 * `hasUnmetMandatory` is false and a creator with issues reads as `flagged` rather than `failed`.
 */
export function deriveVerdict(input: {
  evaluated: number;
  total: number;
  issues: number;
  hasUnmetMandatory?: boolean;
}): ScreeningVerdict {
  if (input.total === 0 || input.evaluated === 0) {
    return 'queued';
  }
  // Still screening outranks "has flags": a verdict on half-screened content isn't a verdict.
  // The flag count is still shown on the card, so nothing is hidden by this.
  if (input.evaluated < input.total) {
    return 'progress';
  }
  if (input.hasUnmetMandatory) {
    return 'failed';
  }
  return input.issues > 0 ? 'flagged' : 'passed';
}

/**
 * Whether the payload attributed issues to specific rules.
 *
 * `adaptNoodleFlags` only ever produces flags when it could read per-rule rows, so any flag at all
 * means per-rule attribution.
 */
export function detectAttribution(flags: readonly ScreeningFlag[]): RuleAttribution {
  return flags.length > 0 ? 'per-rule' : 'counts-only';
}

/**
 * The rule-by-rule state machine for one creator.
 *
 * The important case is the last one: when issues exist but we cannot say which rule they belong
 * to, a rule is `unattributed` — never `met`. Claiming a rule passed on the strength of a count we
 * can't attribute would be the one genuinely misleading thing this screen could do.
 */
export function deriveRuleOutcome(input: {
  rule: NoodleQuestionDto;
  evaluatedCount: number;
  totalIssues: number;
  breachedRuleIds: ReadonlySet<string>;
  attribution: RuleAttribution;
}): RuleOutcome {
  if (input.evaluatedCount === 0) {
    return 'notAssessed';
  }
  if (input.attribution === 'per-rule') {
    return input.breachedRuleIds.has(input.rule.id) ? 'breached' : 'met';
  }
  return input.totalIssues > 0 ? 'unattributed' : 'met';
}

export function toCompletion(evaluated: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((evaluated / total) * 100));
}

/* ---- per-creator summary ----------------------------------------------- */

/**
 * Folds one creator's `screening_results` response into progress figures.
 *
 * Mirrors the maths already proven in `creator-review-item.component.ts`.
 */
export function summariseCreator(
  results: ScreeningResultsResponse | undefined,
): ScreeningCreatorProgress {
  if (!results?.summary) {
    return { ...EMPTY_PROGRESS };
  }

  const content = Array.isArray(results.content) ? results.content : [];
  let issues = 0;
  let flaggedContent = 0;
  for (const item of content) {
    const found = item?.reviewResults?.issueFoundCount ?? 0;
    issues += found;
    if (found > 0) {
      flaggedContent += 1;
    }
  }

  const total = results.summary.contentCount ?? 0;
  const evaluated = results.summary.reviewedCount ?? 0;

  return {
    evaluated,
    total,
    flaggedContent,
    issues,
    completion: toCompletion(evaluated, total),
    verdict: deriveVerdict({ evaluated, total, issues }),
  };
}

/** Rolls the per-creator summaries up into the hub's KPI strip. */
export function summariseCohort(
  progresses: readonly ScreeningCreatorProgress[],
  flagCount: number,
): ScreeningKpis {
  let evaluated = 0;
  let totalContent = 0;
  for (const progress of progresses) {
    evaluated += progress.evaluated;
    totalContent += progress.total;
  }
  return {
    creators: progresses.length,
    evaluated,
    totalContent,
    flags: flagCount,
    // Every flag is "open" until flag decisions exist.
    openFlags: flagCount,
    cohortCompletion: toCompletion(evaluated, totalContent),
  };
}

/* ---- the brief-level flag list ----------------------------------------- */

/** Indexes the rule list so a flag can resolve its rule and its 1-based display number. */
function indexRules(rules: readonly NoodleQuestionDto[]): Map<string, { rule: NoodleQuestionDto; index: number }> {
  const byId = new Map<string, { rule: NoodleQuestionDto; index: number }>();
  rules.forEach((rule, position) => {
    if (rule?.id) {
      byId.set(rule.id, { rule, index: position + 1 });
    }
  });
  return byId;
}

/**
 * A per-rule result row, as the brief-review pipeline shapes it (`ReviewResultsV2`). The screening
 * payload may or may not use the same shape; anything unrecognised is skipped.
 */
function adaptRuleRow(
  row: unknown,
  contentId: string,
  creatorId: string | undefined,
  contentUrl: string | undefined,
  rulesById: Map<string, { rule: NoodleQuestionDto; index: number }>,
): ScreeningFlag | undefined {
  if (!isRecord(row)) {
    return undefined;
  }

  // Only failures are flags. An absent answer is treated as a failure only when the row also
  // carries an explanation — otherwise we cannot tell, and we skip rather than guess.
  const answer = readString(row, 'answer');
  const explanation = readString(row, 'explanation', 'finding');
  const failed = answer ? answer.toLowerCase() !== 'approved' : !!explanation;
  if (!failed) {
    return undefined;
  }

  const ruleId = readString(row, 'id', 'questionId', 'ruleId');
  const match = ruleId ? rulesById.get(ruleId) : undefined;

  return {
    id: `${contentId}::${ruleId ?? readString(row, 'statement') ?? 'rule'}`,
    contentId,
    creatorId,
    rule: match?.rule,
    ruleIndex: match?.index,
    statement: readString(row, 'statement') ?? match?.rule.statement,
    kind: deriveKind(match?.rule),
    severity: deriveSeverity(match?.rule),
    finding: explanation,
    quote: readString(row, 'matchingTranscript', 'matchingOcrOrVisual', 'quote'),
    at: readString(row, 'timestamps', 'at'),
    contentUrl,
  };
}

/** Pulls the content rows out of whichever envelope the payload uses. */
function readContentRows(noodleData: unknown): unknown[] {
  if (Array.isArray(noodleData)) {
    return noodleData;
  }
  if (isRecord(noodleData)) {
    return readArray(noodleData, 'content', 'contents', 'results', 'data');
  }
  return [];
}

/**
 * Turns the brief-level payload into a flat list of flags.
 *
 * Returns `[]` when the payload carries only aggregate counts — the caller should read that as
 * "no per-flag detail available" and show a coming-soon state, not "no problems found".
 */
export function adaptNoodleFlags(
  noodleData: unknown,
  rules: readonly NoodleQuestionDto[],
): ScreeningFlag[] {
  // Belt and braces: the payload is third-party and untyped, so a single unexpected shape must
  // degrade the Content tab to its coming-soon state rather than take the page down.
  try {
    return sortFlagsBySeverity(collectFlags(noodleData, rules));
  } catch {
    return [];
  }
}

function collectFlags(noodleData: unknown, rules: readonly NoodleQuestionDto[]): ScreeningFlag[] {
  const rulesById = indexRules(rules);
  const flags: ScreeningFlag[] = [];

  for (const row of readContentRows(noodleData)) {
    if (!isRecord(row)) {
      continue;
    }
    const contentId = readString(row, 'id', 'contentId') ?? '';
    if (!contentId) {
      continue;
    }
    const creatorId = readString(row, 'creatorId', 'noodleCreatorId');
    const contentUrl = readString(row, 'contentUrl', 'socialUrl', 'url', 'downloadUrl');

    // `reviewResults` is an aggregate object in the per-creator payload and (hopefully) an array of
    // per-rule rows in the issues-only payload. Only the array form carries flag detail.
    const reviewResults = row['reviewResults'];
    const rows = Array.isArray(reviewResults)
      ? reviewResults
      : readArray(row, 'issues', 'flags', 'breaches');

    for (const ruleRow of rows) {
      const flag = adaptRuleRow(ruleRow, contentId, creatorId, contentUrl, rulesById);
      if (flag) {
        flags.push(flag);
      }
    }
  }

  return flags;
}

const SEVERITY_ORDER: Record<FlagSeverity, number> = { high: 0, medium: 1, low: 2, unrated: 3 };

/** The design always lists flags high → medium → low. */
export function sortFlagsBySeverity(flags: readonly ScreeningFlag[]): ScreeningFlag[] {
  return [...flags].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

/* ---- per-content view --------------------------------------------------- */

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v']);
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif']);

/** File extension of a media URL, ignoring query strings and fragments. */
export function contentUrlExtension(url: string): string {
  const path = url.split('?')[0]?.split('#')[0] ?? '';
  const slash = path.lastIndexOf('/');
  const file = slash >= 0 ? path.slice(slash + 1) : path;
  const dot = file.lastIndexOf('.');
  if (dot < 0 || dot === file.length - 1) {
    return '';
  }
  return file.slice(dot + 1).toLowerCase();
}

/** True when the URL points at a video file the content card can play. */
export function isScreeningVideoUrl(url: string): boolean {
  return VIDEO_EXTENSIONS.has(contentUrlExtension(url));
}

/** One URL is a video or image; several URLs are a carousel; none is empty. */
export function classifyScreeningMedia(urls: readonly string[]): ScreeningMediaKind {
  if (urls.length === 0) {
    return 'none';
  }
  if (urls.length > 1) {
    return 'carousel';
  }
  const extension = contentUrlExtension(urls[0]);
  if (VIDEO_EXTENSIONS.has(extension)) {
    return 'video';
  }
  if (IMAGE_EXTENSIONS.has(extension)) {
    return 'image';
  }
  return 'none';
}

function creatorInitials(name: string, provided?: string): string {
  const given = provided?.trim();
  if (given) {
    return given;
  }
  return name
    .replace(/^@/, '')
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function readContentUrls(item: { contentUrls?: readonly string[] } | undefined): string[] {
  const raw = item?.contentUrls;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((url): url is string => typeof url === 'string')
    .map((url) => url.trim())
    .filter((url) => url.length > 0);
}

/** Builds the creator report's content grid from their results plus any flags we resolved. */
export function adaptCreatorContent(
  results: ScreeningResultsResponse | undefined,
  flags: readonly ScreeningFlag[],
): ScreeningContentItem[] {
  let content: ScreeningResultsResponse['content'];
  try {
    content = Array.isArray(results?.content) ? results.content : [];
  } catch {
    return [];
  }
  return content.map((item) => {
    const counts = item?.reviewResults;
    const rulesTotal = counts?.totalCount ?? 0;
    const issues = counts?.issueFoundCount ?? 0;
    const itemFlags = flags.filter((flag) => flag.contentId === item.id);
    const evaluated = rulesTotal > 0;
    const contentUrls = readContentUrls(item);

    let verdict: ScreeningContentItem['verdict'] = 'pending';
    if (evaluated) {
      if (itemFlags.some((flag) => flag.kind === 'missing')) {
        verdict = 'fail';
      } else if (issues > 0) {
        verdict = 'flag';
      } else {
        verdict = 'pass';
      }
    }

    return {
      id: item.id,
      rulesTotal,
      rulesPassed: Math.max(0, rulesTotal - issues),
      issues,
      approvedCount: counts?.approvedCount ?? 0,
      contentUrls,
      mediaKind: classifyScreeningMedia(contentUrls),
      verdict,
      flags: itemFlags,
      contentUrl: itemFlags.find((flag) => flag.contentUrl)?.contentUrl,
    };
  });
}

/** One creator's results, used to collect the posts that raised a red flag. */
export interface FlaggedContentSource {
  name: string;
  initials?: string;
  /** Instagram or TikTok handle, including the @ when the caller has one. */
  handle?: string;
  noodleCreatorId?: string;
  results?: ScreeningResultsResponse;
}

/**
 * Posts across the cohort whose review counts include at least one issue.
 *
 * This is the same set the KPI uses when the brief payload has no per-rule rows.
 * Creators with no results are omitted.
 */
export function collectFlaggedContent(
  creators: readonly FlaggedContentSource[],
  flags: readonly ScreeningFlag[],
): FlaggedCohortContent[] {
  const flagged: FlaggedCohortContent[] = [];
  for (const creator of creators) {
    if (!creator.results) {
      continue;
    }
    const items = adaptCreatorContent(creator.results, flags);
    items.forEach((item, index) => {
      if (item.issues > 0) {
        flagged.push({
          item,
          creatorName: creator.name,
          initials: creatorInitials(creator.name, creator.initials),
          handle: creator.handle,
          noodleCreatorId: creator.noodleCreatorId,
          contentIndex: index + 1,
          contentTotal: items.length,
        });
      }
    });
  }
  return flagged;
}

/** All screened items, or only the ones Noodle marked with at least one issue. */
export function visibleScreeningContent(
  items: readonly ScreeningContentItem[],
  view: ContentView,
): readonly ScreeningContentItem[] {
  if (view === 'issues') {
    return items.filter((item) => item.issues > 0);
  }
  return items;
}
